import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense,
} from 'react';
import * as Sentry from '@sentry/react';
import {
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { reportError } from '../lib/sentry';
import Dashboard from '../views/Dashboard/Dashboard';
import Login from '../views/Login/Login';
import PageNotFound from '../views/PageNotFound/PageNotFound';
import { PatientAuthProvider } from './PatientFormStandalone/PatientAuthContext';

import { RootState } from '~/store/types/sharedTypes';
import { GENERAL_FONT, MHColours } from '../constants/theme';

import { getMyUserInfo } from '../lib/services/generalService';
import {
  logoutAction,
  sessionUserFetchCompleted,
} from '../store/actions/general';
import {
  getAccessToken,
  refreshAccessToken,
  setAccessToken,
} from '../lib/services/token';
import { hasPermission } from '../lib/authz';

import { Loading } from '../views/Login/Loading';
import { setGlobalLogoutAction } from '../lib/services/logoutUtils';
import { assertAdminToken } from '../lib/helpers/auth';
import { useAutoLogout } from '../lib/hooks/useAutoLogout';

import type { AppDispatch } from '../store';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const NewVisitForm = lazy(() => import('./PatientFormStandalone/NewVisitForm'));
const FollowUpForm = lazy(() => import('./PatientFormStandalone/FollowUpForm'));

const INACTIVITY_LOGOUT_MINUTES = 40;

const Main: React.FC = () => {
  const { loading, sessionUser } = useSelector(
    (state: RootState) => state.general
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch<AppDispatch>();

  const isPatientFormRoute = location.pathname.startsWith('/patient-form');
  const pathnameRef = useRef(location.pathname);
  const canWriteTemplates = hasPermission(
    sessionUser,
    'template:writeTemplate'
  );
  const isTechnicalAdmin = hasPermission(sessionUser, 'technicalAdmin');
  const canExportData = hasPermission(sessionUser, 'data:export');

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const handleLogout = useCallback(() => {
    if (pathnameRef.current === '/login') return;
    dispatch(logoutAction(navigateRef.current));
  }, [dispatch]);

  useAutoLogout({
    onLogout: handleLogout,
    timeoutMinutes: INACTIVITY_LOGOUT_MINUTES,
    disabled: isPatientFormRoute,
  });

  useEffect(() => {
    setGlobalLogoutAction(() => dispatch(logoutAction(navigateRef.current)));
  }, [dispatch]);

  useEffect(() => {
    if (isPatientFormRoute) return;

    const fetchUserData = async () => {
      if (sessionStorage.getItem('isLoggedOut') === 'true') {
        setIsAuthenticated(false);
        navigateRef.current('/login');
        return;
      }

      let token = getAccessToken();

      if (!token) {
        token = await refreshAccessToken();
      }

      if (!token) {
        setIsAuthenticated(false);
        navigateRef.current('/login');
        return;
      }
      try {
        assertAdminToken(token);

        const userData = await getMyUserInfo();
        Sentry.setUser({ id: userData._id?.toString() });

        dispatch(sessionUserFetchCompleted(userData));
        setIsAuthenticated(true);
      } catch (error) {
        reportError(error);
        console.error('Error fetching user data:', error);
        sessionStorage.setItem('isLoggedOut', 'true');
        setIsAuthenticated(false);
        setAccessToken(null);
        navigateRef.current('/login');
      }
    };

    fetchUserData();
  }, [dispatch, isPatientFormRoute]);

  useEffect(() => {
    if (sessionUser) {
      setIsAuthenticated(true);
    }
  }, [sessionUser]);

  const renderPermissionGatedDashboard = (hasAccess?: boolean) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (hasAccess) {
      return <Dashboard />;
    }
    return <Navigate to="/dashboard/patients" replace />;
  };

  if (!isPatientFormRoute && (loading || isAuthenticated === null)) {
    return <Loading />;
  }

  return (
    <div
      style={{
        height: '100vh',
        backgroundColor: MHColours.mhBackground,
        fontFamily: GENERAL_FONT,
      }}
    >
      <CssBaseline />
      <GlobalStyles styles={(theme) => ({ body: theme.typography.body2 })} />

      <SentryRoutes>
        <Route
          path="/"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/dashboard"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/dashboard/:id"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/dashboard/admins"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/dashboard/patients"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/dashboard/submissions"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/dashboard/app-version"
          element={renderPermissionGatedDashboard(isTechnicalAdmin)}
        />
        <Route
          path="/dashboard/visit-runs"
          element={renderPermissionGatedDashboard(isTechnicalAdmin)}
        />
        <Route
          path="/dashboard/forms-export"
          element={renderPermissionGatedDashboard(canExportData)}
        />
        <Route
          path="/dashboard/templates"
          element={renderPermissionGatedDashboard(canWriteTemplates)}
        />
        <Route
          path="/login"
          element={
            isAuthenticated &&
            sessionStorage.getItem('isLoggedOut') !== 'true' ? (
              <Navigate to="/dashboard/patients" />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/patient-form/new-visit"
          element={
            <PatientAuthProvider>
              <Suspense fallback={null}>
                <NewVisitForm />
              </Suspense>
            </PatientAuthProvider>
          }
        />
        <Route
          path="/patient-form/followup"
          element={
            <PatientAuthProvider>
              <Suspense fallback={null}>
                <FollowUpForm />
              </Suspense>
            </PatientAuthProvider>
          }
        />
        <Route path="/*" element={<PageNotFound />} />
      </SentryRoutes>
    </div>
  );
};

export default Main;
